import React, { useState } from 'react';
import { navigate, type RouteComponentProps } from '@gatsbyjs/reach-router';
import { Helmet as MetaTags } from 'react-helmet';
import { v4 as uuid } from 'uuid';
import TEST_ID from './index.testid';
import ContentContainerDefault, {
  MAX_CONTAINER_WIDTH,
} from '~/components/molecule/ContentContainer';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { useGetFormsQuery, type SortDirection } from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import Loading from '~/components/atom/Loading';
import FormListTable from './components/Table';
import composeListData from './components/Table/utils/composeData';
import useSortSettings from '~/hooks/useSortSettings';
import formListColumns from './components/Table/utils/formListColumns';
import EmptyStateComponent from '~/components/template/EmptyStateComponent';

import createPageTitle from '~/util/createPageTitle';
import { FORM_SORT_SETTINGS } from '~/util/localStorageKeys';
import useLimit from '~/hooks/useLimit';
import LimitUpgradeCTA from '~/components/organism/LimitUpgradeCTA';
import FormsToolbar from './components/FormsToolbar';
import useRowSelect from '~/components/organism/Table/hooks/useRowSelect';

const text = {
  title: 'Formulieren',
  emptyStateHeader: 'Je hebt nog geen formulieren',
  emptyStateDescription:
    'Verzamel informatie op een manier die het beste werkt voor jou',
  addFormLabel: 'Nieuw formulier',
  importFormLabel: 'Formulier importeren',
  deleteAllForms: 'Delete all forms',
  limitCTABody: {
    approachingLimit:
      'Je bent heel dicht bij de hoeveelheid formulieren die in uw huidige plan zijn toegestaan. Upgrade om toegang te krijgen tot meer formulieren',
    overLimit:
      'Je bent over de hoeveelheid formulieren die in uw huidige plan zijn toegestaan. Upgrade om toegang te krijgen tot meer formulieren',
  },
  approachingLimitButtonLabel: 'Ontvang nu meer formulieren',
};

export const LINK_PREFIX = '/-/forms';

type Props = {} & RouteComponentProps;
const FormsOverview: React.FCC<Props> = () => {
  const [sortSettings, updateSortSettings] =
    useSortSettings<Array<string>>(FORM_SORT_SETTINGS);
  const [version, setVersion] = useState(uuid());
  const account = useCurrentAccount();

  const selectionProps = useRowSelect();
  const { data, loading, networkStatus, error, refetch } = useGetFormsQuery({
    variables: {
      accountId: account.id,
    },
  });
  const formsLimit = useLimit('root.forms.total');

  if (loading) {
    <ContentContainerDefault maxContentWidth={MAX_CONTAINER_WIDTH}>
      <MetaTags>
        <title>{createPageTitle(text.title)}</title>
      </MetaTags>
      <Loading />
    </ContentContainerDefault>;
  }

  const totalEnabledForms =
    data?.getForms.filter(({ enabled }) => enabled === true).length ?? 0;
  const { itemsSelected, deselectAllItems } = selectionProps;

  return (
    <ContentContainerDefault maxContentWidth={MAX_CONTAINER_WIDTH}>
      <MetaTags>
        <title>{createPageTitle(text.title)}</title>
      </MetaTags>

      <JustificationContainer
        align="start"
        justification="start"
        margin={['xl', null, 'xxl', null]}
        direction="column"
      >
        <LimitUpgradeCTA
          limit={formsLimit}
          currentValue={totalEnabledForms}
          textObj={{
            body: text.limitCTABody,
            buttonLabel: text.approachingLimitButtonLabel,
          }}
          margin={['base', null]}
        />
        <FormsToolbar
          limitedFormsToEnable={totalEnabledForms >= formsLimit.limit}
          selectedItems={itemsSelected}
          deselectAllItems={deselectAllItems}
          refreshData={async () => {
            await refetch();
            const nextID = uuid();
            setVersion(nextID);
          }}
        />
      </JustificationContainer>
      {data?.getForms.length === 0 ? (
        <EmptyStateComponent
          dataTestId={TEST_ID.EMPTY_STATE}
          header={text.emptyStateHeader}
          description={text.emptyStateDescription}
          buttonProps={{
            label: text.addFormLabel,
            onClick: () => navigate(`${LINK_PREFIX}/wizard/`),
          }}
          illustration="list"
        />
      ) : (
        <FormListTable
          selectionProps={selectionProps}
          columns={formListColumns((key, direction: SortDirection) => {
            updateSortSettings({
              sortField: key,
              sortDirection: direction,
            });
            setVersion(uuid());
          }, version)}
          data={composeListData(data?.getForms ?? [], version, sortSettings)}
          loading={loading}
          networkStatus={networkStatus}
          error={error != null}
          refreshTable={async () => {
            await refetch();

            const nextID = uuid();
            setVersion(nextID);
          }}
        />
      )}
    </ContentContainerDefault>
  );
};

export default FormsOverview;
